import { defineComponent, VNodeChildren, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { container, namedLink, pageTitle } from '@/ui/components';
import { hasFeature } from '@/ui/auth/auth';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { IssueStatus } from '@/entities/todo';
import { IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { pushRoute, toasted } from '@/ui/helpers';
import { issueEditWithLogs } from '@/ui/pages/issue/issueEditWithLog/IssueEditWithLogs';
import { pageTitleWithTag } from '@/ui/components/PageTitleWithTag';
import { IssueRecord } from '@/entities/issue/v2';
import { ApprovalStatus } from '@/entities/approvals/constants';

export interface IssueEditPageProps {
  id: number;
}

export const IssueEditPage = defineComponent<IssueEditPageProps>({
  name: 'IssueEditPage',
  props: ['id'],
  setup(props) {
    const issue = ref<IssueRecord | null>(null);

    if (!hasFeature('issues')) {
      return renderResourceTeaserPage(t('common.issues'));
    }

    const onEdited = (record: IssueRecordEditable) => {
      if (record.status === IssueStatus.Done) {
        toasted.success(t('issues.resolved'));
      }

      pushRoute({ name: 'issueIndex' });
    };

    const onResetApproval = () => {
      if (issue.value?.data?.approval) {
        issue.value.data.approval.status = ApprovalStatus.Pending;
      }
    };

    const onIssueFetch = (fetchedIssue: IssueRecord) => {
      issue.value = fetchedIssue;
    };

    return function render() {
      return container(
        [
          pageTitleWithTag(
            {
              props: {
                text: t('issues.edit'),
                classes: '',
                status: issue?.value?.data?.approval?.status,
                approvalId: issue?.value?.data?.approval?.id,
              },
              on: {
                'reset:approval': onResetApproval,
              },
            },
          ),
          issueEditWithLogs({
            props: { issueId: props.id },
            on: {
              'edit:issue': onEdited,
              'fetch:issue': onIssueFetch,
            },
          }),
        ],
        'x-large',
      );
    };
  },
});

export const issueEditPageLink = (anchor: VNodeChildren, id: number) => namedLink(
  anchor,
  'issueEdit',
  id.toString(),
);
